import TechSpecsDetail from "@/components/tactsuit/tech-specs/TechSpecsDetail";
import { useTrans } from "@/utils/useTrans";
import { ProductSlug } from "@/api/product-api";
import TechSpecsPreview from "@/components/tactsuit/tech-specs/TechSpecsPreview";
import { cn } from "@/libs/utils";
import { Button } from "@/v2-components/ui/button";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";

/**
 * 점을 **누르면** 뜨는 툴팁 내용(번역 키) — EXOSHOCK 번들 시안 `SpecTooltip` 31769:39163.
 * 미리보기의 점에 하나라도 있으면 그 미리보기는 마우스오버 라벨 · 팝업(motors · controllers · images.json) 대신 툴팁만 쓴다.
 */
export interface SpecTooltipContent {
  title: string;
  copy: string;
  /** 같은 페이지 섹션으로 내려가는 링크 — `anchor` 는 목적지 섹션 id */
  link?: { label: string; anchor: string };
}

export interface Point {
  x: number;
  y: number;
  r: number;
  label: Label;
  tooltip?: SpecTooltipContent;
}

export interface OverlayArea extends Point {
  type?: "circle" | "rect";
  width?: number;
  height?: number;
}

export type Label =
  | "Power Button & LED Indicator"
  | "Haptic Motor * 6"
  | "Magnetic Strap"
  | "Pairing Clip"
  | "Haptic Motor * 4"
  | "PU Leather Foam"
  | "Velcro (Main Controller)"
  | "Velcro (Hook)"
  | "Velcro (Loop)"
  | "Velcro (Foam)"
  | "Mesh Lining"
  | "Shoulder Snap Buttons"
  | "Zipper"
  | "Hanger Loop"
  | "Audio Cable Holder"
  | "Charging Port"
  | "Main Controller"
  | "Side Straps"
  | "Haptic Motor * 32"
  | "Haptic Motor * 16"
  | "Control Button"
  | "Haptic Motor * 3 (each)"
  | "Haptic Motor * 3"
  | "Strap Loop"
  | "Velcro"
  | "Main Strap"
  | "Inner Lining"
  | "Ankle Strap"
  | "Foot Strap"
  | "Velcro Panel"
  | "MOLLE"
  | "Back Controller"
  | "Badge";

export type PreviewType = {
  side: string;
  /**
   * 면 이름 대신 보여 줄 캡션(번역 키). 있으면 데스크톱은 이미지 아래, 모바일은 이미지 아래에 이것을 쓴다.
   * `side` 는 팝업 데이터(motors · controllers · images.json)의 키라 바꾸지 않는다.
   */
  caption?: string;
  imageSrc: string;
  canvasSize: [number, number];
  points: Point[];
};

const previews: Record<ProductSlug, PreviewType[]> = require("./previews.json");

export default function TechSpecs({
  slug,
  previewSlug,
  note,
  productName,
  className,
  useComparator = false,
  onCompare,
}: {
  slug: string;
  /**
   * 미리보기(이미지 · 마커)만 다른 데이터로 — 스펙 표(`TechSpecsDetail`)는 `slug` 그대로.
   * EXOSHOCK 번들: 스펙은 Air 기준인데 앞면은 콜랩 렌더다(피그마 `31584:38643`).
   * ⚠️ 팝업 데이터(motors · controllers · images.json)도 이 키로 찾는다 — 이미지 좌표가 다르면 Air 의 모터 배치를
   * 얹을 수 없어서다. 그 키가 없으면 마커는 라벨만 띄운다.
   */
  previewSlug?: string;
  /** 미리보기 아래 안내 한 줄(번역 키) */
  note?: string;
  productName: string;
  className?: string;
  useComparator?: boolean;
  onCompare?: () => void;
}) {
  const { t } = useTrans(["tactsuit"]);
  const _previews = previews[previewSlug ?? slug] || [];
  const mobile = useIsMobileView();

  if (!_previews.length) return null;

  return (
    <div className={cn("mt-12 laptop:mt-16", className)}>
      <div className="flex items-center justify-center laptop:justify-between laptop:py-6">
        <Title className="text-center laptop:text-left laptop:text-headline-lg">{`${productName} ${t("Specs")}`}</Title>
        {useComparator && !mobile && <Opener onClick={onCompare} />}
      </div>

      <div className="mx-auto mt-5 laptop:mt-6">
        <TechSpecsPreview slug={previewSlug ?? slug} previews={_previews} />
      </div>

      {useComparator && mobile && (
        <div className="mt-5 flex justify-center">
          <Opener onClick={onCompare} />
        </div>
      )}
      {/* 모바일 14/18 ls .25 · 데스크톱 16/22 ls .5, 미리보기와 24(모바일은 버튼과 20) — 피그마 31600:38916 · 31602:38903 */}
      {!!note && (
        <p className="mt-5 text-center text-base tracking-[0.25px] text-coolgray-90 laptop:mt-6 laptop:text-lg laptop:tracking-[0.5px]">
          {t(note)}
        </p>
      )}
      <TechSpecsDetail slug={slug} />
    </div>
  );
}

const Title = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <p className={cn("text-3xl capitalize", className)}>{children}</p>
);

const Opener = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTrans(["tactsuit"]);
  return (
    <Button
      onClick={onClick}
      variant={"blue-fill"}
      className="flex !min-h-10 items-center gap-x-2 rounded-full bg-blue-60 !px-4 !py-2"
    >
      <Title className="text-sm font-600 normal-case text-white">
        {t("Compare with TactSuit")}
      </Title>
      <OpenSVG />
    </Button>
  );
};

const OpenSVG = ({ className }: { className?: string }) => (
  <svg
    className={cn("size-6", className)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="24" height="24" rx="12" fill="white" />
    <path
      d="M10.3356 14.6942H5.4375C5.27788 14.6942 5.14425 14.6404 5.03663 14.5328C4.92888 14.425 4.875 14.2913 4.875 14.1317C4.875 13.9721 4.92888 13.8385 5.03663 13.7308C5.14425 13.6231 5.27788 13.5692 5.4375 13.5692H10.3356L8.60475 11.8384C8.501 11.7346 8.44788 11.6061 8.44538 11.4527C8.443 11.2993 8.49613 11.1645 8.60475 11.0481C8.72113 10.9317 8.85481 10.8735 9.00581 10.8735C9.15669 10.8735 9.29031 10.9317 9.40669 11.0481L12.0159 13.6571C12.0861 13.7274 12.1356 13.8015 12.1644 13.8793C12.1933 13.9572 12.2077 14.0413 12.2077 14.1317C12.2077 14.2221 12.1933 14.3062 12.1644 14.3841C12.1356 14.462 12.0861 14.536 12.0159 14.6063L9.39525 17.227C9.28363 17.3385 9.15306 17.3935 9.00356 17.392C8.85406 17.3906 8.72113 17.3317 8.60475 17.2153C8.49613 17.099 8.43988 16.9673 8.436 16.8203C8.43225 16.6731 8.4885 16.5414 8.60475 16.425L10.3356 14.6942ZM13.6644 10.4322L15.3953 12.163C15.499 12.2668 15.5521 12.3955 15.5546 12.5488C15.557 12.7022 15.5039 12.8371 15.3953 12.9535C15.2789 13.0697 15.1452 13.1278 14.9942 13.1278C14.8433 13.1278 14.7097 13.0697 14.5933 12.9535L11.9841 10.3442C11.9139 10.2741 11.8644 10.2001 11.8356 10.1222C11.8067 10.0443 11.7923 9.96015 11.7923 9.86965C11.7923 9.77928 11.8067 9.69515 11.8356 9.61727C11.8644 9.5394 11.9139 9.4654 11.9841 9.39527L14.6047 6.77459C14.7164 6.66309 14.8469 6.60803 14.9964 6.6094C15.1459 6.6109 15.2789 6.66977 15.3953 6.78602C15.5039 6.9024 15.5601 7.03415 15.564 7.18128C15.5678 7.3284 15.5115 7.46015 15.3953 7.57652L13.6644 9.30734H18.5625C18.7221 9.30734 18.8558 9.36115 18.9634 9.46878C19.0711 9.57653 19.125 9.71015 19.125 9.86965C19.125 10.0293 19.0711 10.163 18.9634 10.2707C18.8558 10.3783 18.7221 10.4322 18.5625 10.4322H13.6644Z"
      fill="#0F62FE"
    />
  </svg>
);
